"use client"

/**
 * LetterboxAd — renders an ad in a black letterbox bar around 16:9 video.
 * Only activates when the video is landscape (aspect ratio > 1.2) so portrait
 * content is never covered. The ad zone is purely the dead black space.
 *
 * One instance renders one bar (`position="top"` or `"bottom"`) — the caller
 * mounts two, each pointed at its own real ad zone, since letterboxing always
 * splits the dead space evenly and rendering the same zone ID into both bars
 * at once is invalid inventory (see the caller's comment for the two zones
 * used).
 *
 * ── Sizing policy (H5) ──────────────────────────────────────────────────
 * Measured across real device sizes, a 16:9 video in a portrait container
 * leaves letterbox bars that are LARGER than the video itself:
 *
 *   device        video   bar
 *   iPhone 14     219px   312px   (ad would be 93px taller than the content)
 *   Android       232px   342px   (110px taller)
 *   iPhone SE     211px   228px   (17px taller)
 *
 * An ad physically bigger than the content it accompanies is the thing that
 * makes a site read as a scam, so the ad box is capped rather than allowed
 * to fill the bar. Note this placement is inherently mobile-only: on a
 * desktop 1440x900 the bar is just 5% of the container, below
 * MIN_BAR_FRACTION, so nothing renders at all.
 *
 * The creative is SCALED to fit its box rather than stretched or clipped.
 * We can't measure inside the sandboxed iframe (no allow-same-origin), but
 * we know the zone's nominal creative size from the outside, which is
 * enough to compute the scale — this recovers the fit-to-box behaviour that
 * was lost when these embeds were moved into sandboxed iframes.
 */

import { useState, useEffect } from "react"
import { VolumeX, Volume2, X } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdCooldownExpired, markAdShown } from "@/lib/ad-cooldown"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { trackZoneClick } from "@/lib/ad-zone-tracking"
import { ZoneImpressionBeacon } from "@/components/ads/ZoneImpressionBeacon"
import { useTranslations } from "next-intl"

interface LetterboxAdProps {
  /** Aspect ratio of the video content (videoWidth / videoHeight) */
  videoAspectRatio: number
  /** Aspect ratio of the container (containerWidth / containerHeight) */
  containerAspectRatio: number
  /** Pixel height of the media container — required for the px-based caps */
  containerHeightPx?: number | null
  /** Nominal size of the ad zone's creative, used to scale it to fit */
  creativeWidth?: number
  creativeHeight?: number
  /** HTML embed / JS tag from ad network — rendered in a sandboxed iframe */
  embedHtml?: string | null
  /** Direct image banner URL */
  bannerImageUrl?: string | null
  /** Click-through URL for banner */
  bannerClickUrl?: string | null
  /** Muted looping video URL for video letterbox ad */
  videoAdUrl?: string | null
  /** Label shown in corner */
  label?: string
  onDismiss?: () => void
  className?: string
  /**
   * Our ad_zones UUID for the zone being rendered, used to report the
   * impression/click. Without it this placement is invisible in ad reporting —
   * which is exactly what it was before: letterbox ("slide-out") ads rendered
   * to real users and recorded nothing at all. NOT the numeric ExoClick id.
   */
  zoneId?: string | null
  /**
   * Which bar this is. Letterboxing splits the dead space evenly top and
   * bottom, so whenever the bottom bar exists the top one does too — but
   * they need their own real, distinct zone each (see the caller's comment
   * on why two instances of the same zone ID is invalid inventory). "bottom"
   * additionally reserves room for the player's own control bar, which only
   * ever overlays the BOTTOM of the frame.
   */
  position?: "top" | "bottom"
}

/** Minimum letterbox bar height (as fraction of container) to bother showing an ad */
const MIN_BAR_FRACTION = 0.08
/** Tallest the ad box may ever be. 120px fits a 320x100 large mobile banner. */
const MAX_AD_HEIGHT_PX = 120
/** Below this nothing standard fits — render nothing rather than a sliver. */
const MIN_AD_HEIGHT_PX = 50
/** Keep clear of the player controls that overlay the bottom of the bar. */
const CONTROLS_RESERVE_PX = 88
/** Standard leaderboard width — don't stretch wider than a real ad unit. */
const MAX_AD_WIDTH_PX = 728

function LetterboxBar({
  embedHtml,
  bannerImageUrl,
  bannerClickUrl,
  videoAdUrl,
  label,
  onDismiss,
  adWidth,
  adHeight,
  creativeWidth,
  creativeHeight,
  scale,
  zoneId,
}: Pick<LetterboxAdProps, "embedHtml" | "bannerImageUrl" | "bannerClickUrl" | "videoAdUrl" | "label" | "onDismiss" | "zoneId"> & {
  adWidth: number
  adHeight: number
  creativeWidth: number
  creativeHeight: number
  scale: number
}) {
  const t = useTranslations("letterboxAd")
  const [adMuted, setAdMuted] = useState(true)

  return (
    <div
      className="relative flex items-center justify-center overflow-hidden bg-black/40 rounded"
      style={{ width: adWidth, height: adHeight }}
    >
      <ZoneImpressionBeacon zoneId={zoneId} />
      {/* Ad label */}
      <span className="absolute top-1 left-1.5 text-[9px] text-white/40 select-none z-10 pointer-events-none">
        {label}
      </span>

      {/* Dismiss */}
      {onDismiss && (
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss() }}
          className="absolute top-0.5 right-0.5 z-10 text-white/40 hover:text-white/80 transition-colors p-0.5"
          aria-label={t("closeAd")}
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {/* Video ad */}
      {videoAdUrl && (
        <>
          <video
            src={videoAdUrl}
            className="w-full h-full object-contain"
            muted={adMuted}
            autoPlay
            loop
            playsInline
          />
          <button
            onClick={(e) => { e.stopPropagation(); setAdMuted((m) => !m) }}
            className="absolute bottom-1 right-1 z-10 bg-black/60 text-white/70 hover:text-white rounded-full p-1 transition-colors"
            aria-label={adMuted ? t("unmuteAd") : t("muteAd")}
          >
            {adMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
          </button>
        </>
      )}

      {/* Banner image */}
      {!videoAdUrl && bannerImageUrl && (
        <a
          href={bannerClickUrl ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full h-full"
          onClick={(e) => {
            e.stopPropagation()
            // Only reportable on this banner path — the js_embed path below
            // renders the network's tag in a cross-origin sandboxed iframe,
            // where clicks are not observable to us by design.
            trackZoneClick({ zoneId })
          }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={bannerImageUrl} alt={t("advertisement")} className="w-full h-full object-contain" />
        </a>
      )}

      {/* HTML/JS embed — sandboxed iframe (allow-scripts only, no
          allow-same-origin/allow-popups/allow-top-navigation). Rendered at
          the creative's natural size and scaled down to fit, so it is never
          stretched (which distorts) or clipped (which hides the CTA).
          dangerouslySetInnerHTML was the original approach here and never
          worked at all — it does not execute <script> tags, and the
          network's embed is a script. */}
      {!videoAdUrl && !bannerImageUrl && embedHtml && (() => {
        // Renamed from `zoneId`: this is ExoClick's own NUMERIC id from the
        // embed tag, and it was shadowing the ad_zones UUID prop of that name.
        // Passing this one to zone-interact would be silently rejected.
        const exoZoneId = extractExoclickZoneId(embedHtml)
        const style = {
          width: creativeWidth,
          height: creativeHeight,
          border: 0,
          display: "block" as const,
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: "center",
          flexShrink: 0,
        }
        // Real cross-origin iframe when this is a recognized ExoClick zone
        // tag — the srcDoc+allow-scripts-only fallback leaves the frame with
        // an opaque origin, which blocks ad-provider.js's cookie write and
        // the creative never renders. See lib/ad-embed.ts.
        return exoZoneId ? (
          <iframe src={exoclickIframeSrc(exoZoneId)} sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" title={t("advertisement")} scrolling="no" style={style} />
        ) : (
          <iframe
            srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${embedHtml}</body></html>`}
            sandbox="allow-scripts"
            title={t("advertisement")}
            scrolling="no"
            style={style}
          />
        )
      })()}
    </div>
  )
}

export function LetterboxAd({
  videoAspectRatio,
  containerAspectRatio,
  containerHeightPx,
  creativeWidth = 900,
  creativeHeight = 250,
  embedHtml,
  bannerImageUrl,
  bannerClickUrl,
  videoAdUrl,
  label = "Ad",
  onDismiss,
  className,
  zoneId,
  position = "bottom",
}: LetterboxAdProps) {
  const hasConsent = useHasConsent()
  // Frequency gate. This placement previously had NONE — it rendered on every
  // media open whose geometry left a bar, which is what made it feel like
  // spam. Computed in a useState initializer (not a bare call) so the verdict
  // is stable for the component's life and can't flip mid-render, and so the
  // hook stays above every early return below.
  const [freqAllows] = useState(() => {
    const store = useAdFrequencyStore.getState()
    if (!store.isEnabled("letterbox_ads")) return false
    return isAdCooldownExpired("letterbox", store.getCooldownMinutes("letterbox_ads"))
  })

  useEffect(() => {
    if (freqAllows) markAdShown("letterbox")
  }, [freqAllows])

  if (!hasConsent) return null
  if (!freqAllows) return null

  // Only show for landscape content with meaningful letterbox bars
  if (videoAspectRatio <= 1.2) return null
  if (!containerHeightPx || containerHeightPx <= 0) return null

  // Top/bottom bars only exist when the video is "taller" relative to its
  // frame than the container is — i.e. videoAR > containerAR (the classic
  // 16:9-in-a-portrait-phone case). When videoAR <= containerAR the
  // letterboxing is left/right, not top/bottom, so the correct bar height
  // here is zero — this component doesn't do pillarbox (see H8). The
  // previous else branch computed `(1 - 1/containerAR) / 2`, a meaningless
  // value that could come out positive and paint an ad bar over the video.
  const barFraction = containerAspectRatio > 0 && videoAspectRatio > containerAspectRatio
    ? (1 - containerAspectRatio / videoAspectRatio) / 2
    : 0

  if (barFraction < MIN_BAR_FRACTION) return null

  const barPx = barFraction * containerHeightPx
  const containerWidthPx = containerAspectRatio * containerHeightPx

  // Controls only ever overlay the bottom edge — the top bar gets the full
  // barPx to work with.
  const controlsReserve = position === "bottom" ? CONTROLS_RESERVE_PX : 0
  const adHeight = Math.min(barPx - controlsReserve, MAX_AD_HEIGHT_PX)
  if (adHeight < MIN_AD_HEIGHT_PX) return null

  const adWidth = Math.min(containerWidthPx, MAX_AD_WIDTH_PX)
  // Never upscale a creative past 1:1 — only shrink to fit.
  const scale = Math.min(1, adWidth / creativeWidth, adHeight / creativeHeight)

  return (
    <div
      className={cn(
        "absolute left-0 right-0 flex items-center justify-center pointer-events-auto z-[5]",
        position === "bottom" ? "bottom-0" : "top-0",
        className,
      )}
      style={position === "bottom" ? { height: barPx, paddingBottom: controlsReserve } : { height: barPx }}
    >
      <LetterboxBar
        embedHtml={embedHtml}
        bannerImageUrl={bannerImageUrl}
        bannerClickUrl={bannerClickUrl}
        videoAdUrl={videoAdUrl}
        label={label}
        onDismiss={onDismiss}
        zoneId={zoneId}
        adWidth={adWidth}
        adHeight={adHeight}
        creativeWidth={creativeWidth}
        creativeHeight={creativeHeight}
        scale={scale}
      />
    </div>
  )
}
